import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { Users, Eye, Globe, Activity, RefreshCw, Loader2, TrendingUp } from 'lucide-react';

function Num({ v, cor = 'text-acre-green' }) {
  return <p className={`font-sora font-black text-3xl ${cor}`}>{Number(v||0).toLocaleString('pt-BR')}</p>;
}

function OnlineDot({ label, count, cor }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full animate-pulse ${cor}`}/>
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <span className={`font-sora font-bold text-lg ${cor.replace('bg-','text-')}`}>{count}</span>
    </div>
  );
}

export default function AdminAnalytics() {
  const [dados,    setDados]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [agora,    setAgora]    = useState(new Date());

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/analytics');
      setDados(data);
      setAgora(new Date());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    carregar();
    const iv = setInterval(carregar, 30000); // auto-refresh 30s
    return () => clearInterval(iv);
  }, [carregar]);

  const ac = dados?.acessos || {};
  const uo = dados?.usuarios_online || {};
  const porDia = dados?.acessos_por_dia || [];

  // Mínimo e máximo para escala do gráfico
  const maxDia = Math.max(...porDia.map(d => d.total), 1);

  return (
    <Layout title="📊 Analytics do Site" subtitle="Visitantes, acessos e usuários em tempo real">

      {/* Header com atualização */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"/>
          <span className="text-xs text-gray-400">
            Atualizado às {agora.toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit',second:'2-digit'})}
            {' · '}auto-refresh 30s
          </span>
        </div>
        <button onClick={carregar} disabled={loading}
          className="flex items-center gap-1.5 text-xs border border-gray-200 hover:border-acre-green hover:text-acre-green px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''}/> Atualizar
        </button>
      </div>

      {loading && !dados ? (
        <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-acre-green"/></div>
      ) : (
        <>
          {/* ── Cards de acessos ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Eye size={15} className="text-acre-green"/>
                <p className="text-xs font-semibold text-gray-500">Acessos hoje</p>
              </div>
              <Num v={ac.hoje} cor="text-acre-green"/>
              <p className="text-xs text-gray-400 mt-1">{ac.ips_unicos_hoje} IPs únicos</p>
            </div>
            <div className="card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity size={15} className="text-blue-500"/>
                <p className="text-xs font-semibold text-gray-500">Última hora</p>
              </div>
              <Num v={ac.ultima_hora} cor="text-blue-600"/>
              <p className="text-xs text-gray-400 mt-1">acessos recentes</p>
            </div>
            <div className="card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Globe size={15} className="text-purple-500"/>
                <p className="text-xs font-semibold text-gray-500">Total geral</p>
              </div>
              <Num v={ac.total_geral} cor="text-purple-600"/>
              <p className="text-xs text-gray-400 mt-1">desde o início</p>
            </div>
            <div className="card p-4 border-2 border-green-200 bg-green-50/30">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"/>
                <p className="text-xs font-semibold text-gray-500">Online agora</p>
              </div>
              <Num v={dados?.online_agora} cor="text-green-600"/>
              <p className="text-xs text-gray-400 mt-1">visitantes ativos (5min)</p>
            </div>
          </div>

          {/* ── Usuários logados online ── */}
          <div className="grid md:grid-cols-2 gap-5 mb-6">
            <div className="card p-5">
              <div className="flex items-center gap-2 mb-4">
                <Users size={15} className="text-acre-green"/>
                <h2 className="font-sora font-bold text-sm text-acre-dark">Usuários logados online</h2>
                <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                  ao vivo
                </span>
              </div>
              <OnlineDot label="Total de usuários online" count={uo.total_online||0} cor="bg-green-500"/>
              <OnlineDot label="Candidatos online" count={uo.candidatos_online||0} cor="bg-acre-green"/>
              <OnlineDot label="Empresas online" count={uo.empresas_online||0} cor="bg-blue-500"/>
              <p className="text-xs text-gray-400 mt-3">
                * Usuários que realizaram alguma ação nos últimos 5 minutos
              </p>
            </div>

            {/* Gráfico de barras — acessos por dia */}
            <div className="card p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={15} className="text-blue-500"/>
                <h2 className="font-sora font-bold text-sm text-acre-dark">Acessos por dia (14 dias)</h2>
              </div>
              {porDia.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-6">Nenhum dado ainda</p>
              ) : (
                <div className="flex items-end gap-1 h-32">
                  {porDia.slice(-14).map((d, i) => {
                    const pct = (d.total / maxDia) * 100;
                    const dia = new Date(d.dia).toLocaleDateString('pt-BR',{day:'2-digit',month:'2-digit'});
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full bg-acre-green/80 rounded-t transition-all hover:bg-acre-green cursor-pointer relative group"
                          style={{ height: `${Math.max(pct, 4)}%` }}>
                          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-1.5 py-0.5 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            {d.total}
                          </div>
                        </div>
                        <span className="text-[9px] text-gray-400 whitespace-nowrap">{dia}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Nota sobre SEO */}
          <div className="card p-4 bg-blue-50 border border-blue-100">
            <p className="text-xs font-semibold text-blue-700 mb-2">🔍 SEO configurado</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-blue-600">
              {['Meta tags (título, descrição, keywords)','Open Graph (WhatsApp/Facebook)','Schema.org JSON-LD','robots.txt + sitemap.xml'].map(s => (
                <div key={s} className="flex items-center gap-1">
                  <span className="text-green-500">✓</span> {s}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </Layout>
  );
}
